import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {IElement} from '../../../../incase-domain/elements';
import {CORRECT_ANSWER_COLOR, DEBATABLE_ANSWER_COLOR, INCORRECT_ANSWER_COLOR} from '../../../../incase-domain/common/color';
import ColorSelectCompact from '../../../../incase-domain/components/select/compact/color/colorSelectCompact/ColorSelectCompact';

export enum COLOR_FIELDS {
    BACKGROUND_COLOR = 'color',
    FONT_COLOR = 'fontColor',
    CORRECT_COLOR = 'correctColor',
    INCORRECT_COLOR = 'incorrectColor',
    CORRECT_COMMENT_COLOR = 'correctCommentColor',
    INCORRECT_COMMENT_COLOR = 'incorrectCommentColor',
    DEBATABLE_COLOR = 'debatableColor',
    DEBATABLE_COMMENT_COLOR = 'debatableCommentColor',
    BORDER_COLOR = 'borderColor',
    QUESTION_BACKGROUND_COLOR = 'questionBackgroundColor',
    TRY_BUTTON_TEXT_COLOR = 'tryLabelColor',
}

const getColorFieldAccessor = (type: COLOR_FIELDS): ((el: IElement) => string | undefined) => {
    switch (type) {
        case COLOR_FIELDS.FONT_COLOR:
            return (he) => ('font' in he ? he.font?.color : '');
        case COLOR_FIELDS.BACKGROUND_COLOR:
            return (he) => ('color' in he ? he.color : '');
        case COLOR_FIELDS.CORRECT_COLOR:
            return (he) => ('correctColor' in he ? he.correctColor : CORRECT_ANSWER_COLOR);
        case COLOR_FIELDS.INCORRECT_COLOR:
            return (he) => ('incorrectColor' in he ? he.incorrectColor : INCORRECT_ANSWER_COLOR);
        case COLOR_FIELDS.CORRECT_COMMENT_COLOR:
            return (he) => ('correctCommentColor' in he ? he.correctCommentColor : CORRECT_ANSWER_COLOR);
        case COLOR_FIELDS.INCORRECT_COMMENT_COLOR:
            return (he) => ('incorrectCommentColor' in he ? he.incorrectCommentColor : INCORRECT_ANSWER_COLOR);
        case COLOR_FIELDS.DEBATABLE_COLOR:
            return (he) => ('debatableColor' in he ? he.debatableColor : DEBATABLE_ANSWER_COLOR);
        case COLOR_FIELDS.DEBATABLE_COMMENT_COLOR:
            return (he) => ('debatableCommentColor' in he ? he.debatableCommentColor : DEBATABLE_ANSWER_COLOR);
        case COLOR_FIELDS.QUESTION_BACKGROUND_COLOR:
            return (he) => ('questionBackgroundColor' in he ? he.questionBackgroundColor : '');
        case COLOR_FIELDS.TRY_BUTTON_TEXT_COLOR:
            return (he) => ('tryLabelColor' in he ? he.tryLabelColor : '');
        case COLOR_FIELDS.BORDER_COLOR:
            return (he) => ('borderColor' in he ? he.borderColor : '');
        default:
            return () => '';
    }
};

interface FontAndBackgroundColorPickerProps {
    highlightedElement: IElement;
    highlightedElementChangeHandler: {single: (field: string, value: unknown) => void};
    colorFields: COLOR_FIELDS[];
    placeholder?: string;
}

FontAndBackgroundColorPicker.defaultProps = {
    placeholder: '',
};

function FontAndBackgroundColorPicker(props: FontAndBackgroundColorPickerProps): ReactElement {
    const {placeholder, colorFields, highlightedElement, highlightedElementChangeHandler} = props;
    const {t} = useTranslation();

    return (
        <div className="u-flex u-flex-col u-gap-2">
            {colorFields.map((colorField, index) => (
                <div className="u-flex u-h-between u-v-center" key={`${colorField}-${index}`}>
                    <ColorSelectCompact
                        label={placeholder || t(`designSidepanel.${colorField}`)}
                        color={getColorFieldAccessor(colorField)(highlightedElement)}
                        onChange={(color) => {
                            if (colorField === 'fontColor' && 'font' in highlightedElement) {
                                highlightedElementChangeHandler.single('font', {...highlightedElement.font, color});
                            } else {
                                highlightedElementChangeHandler.single(colorField, color ?? '');
                            }
                        }}
                    />
                </div>
            ))}
        </div>
    );
}

export default FontAndBackgroundColorPicker;
